// KIADÁSOK (bejövő számlák és költségek) — a PÉNZÜGY lap kiadás-oldalának szerződése.
//
// A forrás-igazság az Odin `billing/expenses` + `billing/expense-categories` (INTERNAL) erőforrása; ez a fájl
// a BFF↔felület szerződése. A PÉNZ mindenhol **Decimal-STRING** (soha `number`: a JSON number lebegőpontos, és
// egy költség kerekítési hibája valódi pénz), a DÁTUM pedig **nap-pontos `YYYY-MM-DD`** (a buszra a közös
// `bus-day` alakítja ISO-DateTime-ra).
import { addDecimals, INVOICE_ISSUER_KEYS, normalizeDecimal } from '@atlas/shared';
import { z } from 'zod';
import { toQueryArray } from '../_shared/schemas.js';
/** Decimal-string (max 2 tizedes, lehet negatív — a sztornó/jóváíró sor összege negatív). */
const decimalStr = z
    .string()
    .regex(/^-?\d+(\.\d{1,2})?$/, 'Érvénytelen összeg (max 2 tizedes, Decimal-string)');
/** Nap-pontos dátum a szerződésben (a buszra a `bus-day` alakítja teljes ISO-DateTime-ra). */
const dayStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Érvénytelen dátum (YYYY-MM-DD)');
/**
 * A kiadás FORRÁSA — és egyben a szerkeszthetőség kapuja:
 *  • `nav`    — a NAV Online Számla bejövő adatszolgáltatásából importált sor. Az összeg, a szállító és a
 *               számlaszám az ADÓHATÓSÁG rekordja: ezeket a BFF nem engedi átírni (csak kategória + megjegyzés).
 *  • `manual` — kézzel rögzített költség (külföldi szállító, nyugta, adószám nélküli számla — ezek a NAV-ban
 *               SOHA nem jelennek meg); minden mezője szerkeszthető.
 */
export const ExpenseSourceSchema = z.enum(['nav', 'manual']);
/**
 * A NAV-művelet: a módosító és a sztornó bizonylat ÖNÁLLÓ számlaként érkezik, az eredetire hivatkozva.
 * A havi összeg ezért nem egyszerű összegzés — a felület a hivatkozást is mutatja.
 */
export const NavOperationSchema = z.enum(['CREATE', 'MODIFY', 'STORNO']);
/** Kétnyelvű név: a `hu` kötelező, az `en` opcionális (a felület `hu`-ra esik vissza). */
export const ExpenseCategoryNameSchema = z.object({
    hu: z.string().trim().min(1, 'A magyar név kötelező').max(120),
    en: z.string().trim().max(120).nullable().optional(),
});
/**
 * A kiadás-kategória fa MAXIMÁLIS mélysége: **1 = főkategória · 2 = alkategória · 3 = tétel**.
 *
 * A szerződésben él, mert MINDKÉT oldal ismeri: a BFF ezzel utasítja el a negyedik szintet (422), a felület
 * pedig ezzel dönti el, MELY csomópontokat kínálja fel szülőnek — a felület nem ajánlhat olyat, amit a fal
 * elutasít.
 */
export const MAX_CATEGORY_DEPTH = 3;
/**
 * A KIADÁS-oldalon választható kibocsátók — a két valódi adózónk.
 *
 * Szűkebb, mint a számla-oldali `INVOICE_ISSUER_KEYS`: ott a `none` a számlatömb-prefix HIÁNYÁT jelenti
 * (ugyanaz az adózó, mint a `MOR`), a kiadás-oldalon viszont két KÜLÖN adózó van, és a sornak el kell
 * dőlnie, melyikébe tartozik. A készlet a szerződésben él, mert a kézi űrlap és a tömeges import
 * UGYANAZT kell kínálja — két másolat elcsúszna.
 */
export const EXPENSE_ISSUER_KEYS = ['MOR', 'MDIG'];
/**
 * A kiadás-oldalon kínált devizák. Ugyanabból az okból él a szerződésben, mint a kibocsátó-készlet: a kézi
 * űrlapnak és a tömeges importnak UGYANAZT kell felkínálnia. (A szerződés maga bármely 3 betűs kódot
 * elfogad — ez a KÍNÁLAT, nem a fal: egy ritka devizájú számla kézzel is rögzíthető marad.)
 */
export const EXPENSE_CURRENCIES = ['HUF', 'EUR', 'USD'];
/**
 * EGY TÖMEGES IMPORT felső sor-határa.
 *
 * MIÉRT VAN PLAFON, és miért pont itt: az Odin resource-busza **600 kérés/perc** fölött fojt, és a 429
 * korábban NÉMA adatvesztés volt (mért eset a havi számla-generáláson). Egy sor = egy busz-írás, tehát a
 * plafon az, ami garantálja, hogy egyetlen feltöltés nem futhat bele. A mért várakozás 100 sor alatt van,
 * az 500 ennek az ötszöröse — ha valaha szűk lenne, az lapozó importot kíván, nem nagyobb számot.
 */
export const MAX_IMPORT_ROWS = 500;
/**
 * Egy kategória-sor a fából. A fa LEGFELJEBB HÁROM szintű: **főkategória → alkategória → tétel**.
 *
 * A szint nem kötelező: van főkategória, ami alatt semmi nincs, és van, ami alatt több alkategória áll,
 * azok alatt pedig több tétel. A kiadás BÁRMELYIK szintre besorolható — a besorolás mindig egy konkrét
 * csomópont, nem feltétlenül levél.
 */
export const ExpenseCategoryRowSchema = z.object({
    id: z.string(),
    name: ExpenseCategoryNameSchema,
    /** `null` → főkategória. Egyébként a szülő azonosítója (alkategória szülője főkategória, tételé alkategória). */
    parentId: z.string().nullable(),
    version: z.number(),
    /** Hány kiadás hivatkozik rá (a törlés-kapu és a felület jelzése) — a BFF számolja. */
    expenseCount: z.number(),
    /**
     * BÉR TÍPUSÚ TÉTEL: az ide sorolt költségnél a felület felkínálja a munkatárs megadását, máshol nem.
     *
     * ⚠️ CSAK TÉTELEN (levélen) állhat, és NEM ÖRÖKLŐDIK (user-döntés): egy fő- vagy alkategória nem jelölhető,
     * mert oda kiadás amúgy sem sorolható (a besorolás-választó csak leveleket kínál). A jelölés tehát ott ül,
     * ahol a költség is — a kapu egyetlen mező olvasása, nem fa-bejárás.
     */
    requiresEmployee: z.boolean(),
});
export const ExpenseCategoryWriteBodySchema = z.object({
    name: ExpenseCategoryNameSchema,
    parentId: z.string().uuid().nullable().optional(),
    /**
     * BÉR TÍPUSÚ TÉTEL jelölése. Hiányzó mező → CREATE-en `false` (a bevezetés egyetlen meglévő kategóriát sem
     * változtat meg), UPDATE-en a MEGLÉVŐ érték marad („ne nyúlj hozzá").
     *
     * A szerver 422-vel zárja, ha a jelölt csomópontnak gyereke van (vagy lenne) — bér csak TÉTELEN állhat.
     */
    requiresEmployee: z.boolean().optional(),
});
/** Egy kiadás-sor a listán. */
export const ExpenseRowSchema = z.object({
    id: z.string(),
    source: ExpenseSourceSchema,
    /**
     * MELYIK CÉGÜNK kapta a számlát (`MOR` = MORGENS Design · `MDIG` = MORGENS Digital).
     *
     * MIÉRT TÁROLT, és miért nem levezethető: a NAV-adatszolgáltatás ADÓZÓNKÉNT megy — a technikai felhasználó
     * egy adózó nevében jár el —, és a digest a VEVŐ adószámát nem közli (nem is kell: a kérdés már arra
     * szólt). A hovatartozás tehát csak abból tudható, melyik kulccsal kérdeztünk; az import ezt rögzíti.
     */
    issuerKey: z.string(),
    supplierName: z.string(),
    supplierTaxNumber: z.string().nullable(),
    /**
     * A bizonylat sorszáma; `null` → NINCS SZÁMLA (nyugta, bizonylat nélküli költség). A felület ezt külön
     * jelöléssel viszi fel, és üres stringet SOHA nem ír a helyére: az értéknek látszana.
     */
    invoiceNumber: z.string().nullable(),
    navOperation: NavOperationSchema.nullable(),
    originalInvoiceNumber: z.string().nullable(),
    /**
     * SZTORNÓZVA: annak a bizonylatnak a SZÁMLASZÁMA, amelyik ezt a sort semmissé teszi; `null` → nincs ilyen.
     *
     * A `originalInvoiceNumber` PÁRJA, de a MÁSIK irányból: az a sztornó soron áll és visszafelé mutat, ez az
     * EREDETI soron és előre. Nem tárolt mező — a BFF a `STORNO` művelet + `originalInvoiceNumber` párosításból
     * vezeti le (kibocsátó + szállító adószáma + számlaszám kulcson). Tárolva csak elcsúszni tudna a NAV
     * adatától; így a jelölés magától megszűnik, ha a sztornó sor mégsem létezik.
     *
     * MIÉRT KELL A FELÜLETEN: a sztornó soron ott a `STORNO` jelvény és az eredeti száma, az EREDETI soron
     * viszont semmi nem mondaná meg, miért került az elvetettek közé.
     */
    stornoedByInvoiceNumber: z.string().nullable(),
    issueDate: dayStr,
    fulfillmentDate: dayStr.nullable(),
    dueDate: dayStr.nullable(),
    netAmount: decimalStr,
    vatAmount: decimalStr,
    grossAmount: decimalStr,
    currency: z.string(),
    categoryId: z.string().nullable(),
    /**
     * VONATKOZÁSI HÓNAP (`YYYY-MM`) — MELYIK hónap eredményét terheli a tétel (eredményszemlélet).
     *
     * Mindig KITÖLTÖTT a válaszban: ha a soron nincs kézi érték, a BFF a bizonylatból származtatja
     * (teljesítés dátuma, annak híján a kiállításé). A `accountingMonthManual` mondja meg, melyikről van szó —
     * enélkül a felület nem tudná megkülönböztetni a „valaki átsorolta" esetet az alapértelmezettől.
     */
    accountingMonth: z.string(),
    /** `true` → a hónapot KÉZZEL állították (nem a bizonylatból származik). */
    accountingMonthManual: z.boolean(),
    /**
     * A BIZONYLAT hónapja (`YYYY-MM`) — a teljesítés dátumából, annak híján a kiállításéból. A kézi
     * vonatkozási hónap erre SOHA nem hat.
     *
     * MIÉRT KÜLÖN MEZŐ, és miért nem a felület számolja: a hónap-cella ebből jelöli meg, melyik a „bizonylat
     * szerinti" érték (az átsorolás visszavonásának útja), és a lista hónap-szűrése ugyanebből a szabályból
     * származik. Kliens-oldali levezetéssel a szabály két helyen élne, és a kettő elcsúszhatna egymástól.
     */
    documentMonth: z.string(),
    /** A besorolás csomópontjának feloldott, kétnyelvű neve — a BFF oldja fel. */
    categoryName: ExpenseCategoryNameSchema.nullable(),
    /**
     * A besorolás ŐSEI, a GYÖKÉRTŐL a közvetlen szülőig (a saját nevét NEM tartalmazza). Így a felület a
     * teljes ágat ki tudja írni („IT / Tárhely" a „Domain" tétel fölött), akárhány szintű is a fa.
     *
     * MIÉRT TÖMB, és miért nem egy `parentCategoryName`: a fa HÁROM szintű lett (főkategória → alkategória →
     * tétel), és egyetlen szülő-név egy tételnél a KÖZÉPSŐ szintet mondaná meg, a főkategóriát elhallgatva —
     * a lista pedig épp a hovatartozást mutatja. Üres tömb = a besorolás maga főkategória (vagy nincs besorolás).
     */
    categoryPath: z.array(ExpenseCategoryNameSchema),
    /**
     * A besorolás ÁGÁNAK GYÖKERE (a legfelső ős, vagy a saját id-ja, ha maga is főkategória); `null`, ha
     * nincs besorolás. Az ÖSSZEGZÉS kategória-bontása ezen csoportosít — a kimutatás a főkategóriák szintjén
     * olvasható, nem húsz levél-tételen.
     */
    rootCategoryId: z.string().nullable(),
    note: z.string().nullable(),
    /**
     * `true` → a NAV NEM közölt összeget erre a bizonylatra (egyszerűsített számla: a digest se nettót, se
     * áfát nem hordoz). A tárolt `0.00` ilyenkor NEM az összeg, hanem a hiány — a felület ezért nem forintot
     * ír ki, hanem jelzi, hogy nem tudjuk. Valódi 0 összegű bizonylat is van, a kettő nem mosható össze.
     */
    amountUnknown: z.boolean(),
    /**
     * `true` → az `amountUnknown` soron VALAKI KÉZZEL pótolta az összeget (`PUT /expenses/:id/amount`).
     *
     * Nem tárolt mező: a két tényből VEZETJÜK LE — a NAV nem közölt összeget, az import ilyenkor `0.00`-t ír,
     * tehát ha mégis áll ott összeg, azt ember írta oda. Így nincs új Odin-mező (se migráció, se Schema
     * Registry sor), és az állapot nem is hazudható: bekapcsolni csak valódi összeg beírásával lehet.
     * A felület ezért mutathatja az összeget ÚGY, hogy közben jelzi: ez a mi kiegészítésünk, nem a NAV adata.
     */
    amountManual: z.boolean(),
    /**
     * ELVETVE (időbélyeg, `null` = aktív). Az elvetett sor a listából ÉS minden számításból kiesik — nem
     * akarjuk besorolni, és a havi összegbe sem tartozik —, de MEGMARAD és bármikor visszavehető. Törölni
     * nem lehetne: a következő NAV-import úgyis visszahozná.
     */
    dismissedAt: z.string().nullable(),
    /**
     * MELYIK MUNKATÁRS kiadása (`platform.identities.id`); `null` = nincs személyhez kötve.
     *
     * MIÉRT KÜLÖN MEZŐ, és miért nem a kategória hordozza: a kategória a KÖLTSÉG-TÍPUS dimenziója, ez a
     * SZEMÉLYÉ. A bér négy külön ág alatt is megjelenik (Advisory / Management / Product / R&D), tehát a
     * fából nem olvasható ki, kihez tartozik — a „mennyibe kerül egy kollégánk" kérdés viszont pont ezt kéri.
     *
     * A `null` a NORMÁL eset: a költségek többsége (bérleti díj, licenc, könyvelés) nem köthető személyhez.
     */
    employeeIdentityId: z.string().nullable(),
    /**
     * A TÖMEGES IMPORT kötegének azonosítója, vagy `null` (kézi / NAV-sor). A felület ebből rajzol
     * „importált" jelölést, és ezzel szűri le a köteg sorait a jelentésből.
     *
     * MIÉRT NEM ÚJ `source` ÉRTÉK: a `source` ma azt is eldönti, hogy egy sor szerkeszthető-e (a NAV-soré
     * nem). Egy harmadik érték minden `source === 'manual'` ágat érintene, és egy kihagyott ág csendben
     * olvashatóvá merevítené az importált sorokat. A köteg-azonosító jelenléte ugyanazt a jelölést adja,
     * és nem nyúl semmihez.
     */
    importBatchId: z.string().nullable(),
    /** A munkatárs feloldott neve — a BFF oldja fel, hogy a lista ne kérjen külön címtárat. */
    employeeName: z.string().nullable(),
    version: z.number(),
});
/**
 * Az ELVETETT sorok kezelése a listán. Alapértelmezés: `hidden` — ez a lényeg, hogy ne legyenek útban.
 * A `only` a „mit vetettünk el?" visszanéző nézet, az `all` az összevetés.
 */
export const ExpenseDismissedFilterSchema = z.enum(['hidden', 'only', 'all']);
/**
 * A lista szűrői. A `month` `YYYY-MM`, és a hónap-hovatartozást a VONATKOZÁSI hónap dönti el (kézi érték,
 * annak híján a bizonylat hónapja) — ugyanaz a dimenzió, amin a lap összege és az eredménykimutatás áll.
 *
 * A `categoryId` HÁROM értéket ismer: konkrét id · `none` (KATEGORIZÁLATLAN — ez a lap fő teendő-szűrője) ·
 * hiányzó (mind). A „kategorizálatlan" SZÁNDÉKOSAN nem külön státusz-mező, hanem `categoryId IS NULL`.
 */
/**
 * A rendezhető oszlopok — WHITELIST, nem szabad szöveg. A rendezés a BFF memóriájában megy (a hónap amúgy is
 * teljesen betöltött), de a kulcs így sem lehet nyers felhasználói szöveg: egy nyitott mező mező-nevet
 * szivárogtatna, és a kliens-szerződés is elcsúszna a szerver-oldali képlettől.
 */
export const ExpenseSortSchema = z.enum([
    'supplierName',
    'invoiceNumber',
    'issueDate',
    'fulfillmentDate',
    'dueDate',
    'netAmount',
    'grossAmount',
    'category',
    'accountingMonth',
]);
export const ExpenseListQuerySchema = z.object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Érvénytelen hónap (YYYY-MM)'),
    /** KIBOCSÁTÓ-szűrő: melyik cégünk számláit nézzük (`MOR` · `MDIG`). Hiányzó érték → mind. */
    issuerKey: z.string().optional(),
    categoryId: z.string().optional(),
    source: ExpenseSourceSchema.optional(),
    /** Egy IMPORT-KÖTEG sorai (az import jelentéséből ide navigál a felület). */
    importBatchId: z.string().uuid().optional(),
    /**
     * `true` → CSAK azok a sorok, amelyek BÉR-TÍPUSÚ tételbe vannak sorolva, de nincs rajtuk munkatárs.
     * A lap teendő-jelzése (mint a „besorolásra vár"), nem import-specifikus: a kézzel hiányosan felvett
     * sort ugyanúgy megfogja, és addig marad, amíg valaki ki nem tölti.
     */
    missingEmployee: z.coerce.boolean().optional(),
    /**
     * `true` → CSAK a még pótolatlan összegű sorok: a NAV nem közölt összeget, és kézzel sem pótolta senki.
     * Az összegző „összeg nélküli" számlálójának szűrője — ugyanazon a predikátumon, tehát a kattintás után a
     * lista pontosan annyi sort ad, amennyit a szám mond.
     */
    missingAmount: z.coerce.boolean().optional(),
    /** Alapértelmezés (hiányzó érték): `hidden` — az elvetett sorok nem jelennek meg. */
    dismissed: ExpenseDismissedFilterSchema.optional(),
    q: z.string().optional(),
    /**
     * RENDEZÉS. Az alap `issueDate` / `desc` — a legfrissebb bizonylat elöl.
     *
     * A rendezés NEM csak kényelmi funkció: a lapozás offset-alapú, a BFF pedig lapkérésenként újraolvassa a
     * hónapot a buszról. Definiált sorrend nélkül két lap-kérés MÁS sorrendet kaphat, és a „Továbbiak" a
     * szelet-határon ismételne vagy kihagyna sorokat.
     */
    sort: ExpenseSortSchema.optional(),
    order: z.enum(['asc', 'desc']).optional(),
    offset: z.coerce.number().int().min(0).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    /**
     * A MUNKATÁRS-NEVEK feloldásának nyelve (a név SORRENDJE nyelvfüggő: hu → `Vezetéknév Keresztnév`).
     * Hiányzó érték → `hu`. Ugyanaz a paraméter, amivel a hozzárendelés-választók is dolgoznak.
     */
    lang: z.enum(['hu', 'en']).optional(),
});
/**
 * Kézi kiadás felvétele/szerkesztése. NAV-soron a BFF CSAK a `categoryId`-t és a `note`-ot engedi — a többi
 * mező az adóhatóság rekordja (a szerver 422-vel utasítja el a módosításukat, nem csendben hagyja figyelmen kívül).
 */
export const ExpenseWriteBodySchema = z.object({
    /**
     * MELYIK CÉGÜNK kapta a számlát. KÖTELEZŐ a kézi felvitelen: e nélkül a sorról nem derülne ki, melyik cég
     * költsége — és a kibocsátó-szűrő alatt egyik nézetben sem jelenne meg. A NAV-soron az import tölti ki.
     */
    issuerKey: z.enum(INVOICE_ISSUER_KEYS),
    supplierName: z.string().trim().min(1, 'A szállító neve kötelező').max(255),
    supplierTaxNumber: z.string().trim().max(32).nullable().optional(),
    /**
     * A bizonylat sorszáma, vagy `null` — NINCS SZÁMLA (nyugta, bizonylat nélküli költség; a felvitelen
     * külön jelölés).
     *
     * MIÉRT MARAD KÖTELEZŐ A `issueDate`: a kiadás VONATKOZÁSI HÓNAPJA ebből származik (kézi hónap híján),
     * és ezen áll a lap összege meg az eredménykimutatás. Dátum nélkül a sor egyetlen hónaphoz sem tartozna,
     * tehát a számlaszám elhagyása a dátumot NEM teszi elhagyhatóvá.
     */
    invoiceNumber: z.string().trim().max(100).nullable(),
    issueDate: dayStr,
    fulfillmentDate: dayStr.nullable().optional(),
    dueDate: dayStr.nullable().optional(),
    netAmount: decimalStr,
    vatAmount: decimalStr,
    grossAmount: decimalStr,
    currency: z.string().length(3),
    categoryId: z.string().uuid().nullable().optional(),
    /**
     * A vonatkozási hónap KÉZI beállítása (`YYYY-MM`), vagy `null` → vissza a bizonylatból származtatottra.
     * NAV-soron is engedett (mint a besorolás): a bizonylat mezőihez nem nyúlunk, de azt, hogy MELYIK hónap
     * eredményét terheli, nekünk kell tudnunk eldönteni.
     */
    accountingMonth: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'A vonatkozási hónap alakja YYYY-MM')
        .nullable()
        .optional(),
    note: z.string().trim().max(2000).nullable().optional(),
    /** MELYIK MUNKATÁRS kiadása, vagy `null` → nincs személyhez kötve. NAV-soron is engedett (mint a besorolás). */
    employeeIdentityId: z.string().uuid().nullable().optional(),
});
/**
 * BRUTTÓ = NETTÓ + ÁFA — SZERVER-oldali fal.
 *
 * A felület a bruttót SZÁMOLJA (nincs harmadik, kézzel írt szám), de az csak a felület. A szerződés-fal
 * nélkül egy a felületet megkerülő kérés (`net: 100000`, `vat: 27000`, `gross: 1`) átment volna, és onnantól
 * a havi összegző-csík és a kategória-bontás bruttó oszlopa NÉMÁN hibás végösszeget hozott volna — pénzügyi
 * adat, amit semmi nem javít vissza. A NAV-import ág maga számolja a bruttót, tehát ott nem volt rés; a
 * KÉZI írás-út volt nyitva.
 *
 * Fixpontos összehasonlítás (`@atlas/shared`), sosem float: a `0.1 + 0.2` itt valódi hamis riasztás lenne.
 */
export const ExpenseWriteBodySchemaChecked = ExpenseWriteBodySchema.refine((v) => addDecimals(v.netAmount, v.vatAmount) === normalizeDecimal(v.grossAmount), {
    message: 'A bruttó összeg nem egyezik a nettó és az áfa összegével.',
    path: ['grossAmount'],
});
/**
 * ÖSSZEG-PÓTLÁS a NAV-soron — SZŰK kivétel a NAV-fal alól, és csak ott, ahol a NAV nem hagyott mást.
 *
 * MIÉRT KELL: az EGYSZERŰSÍTETT számla összegét a NAV digestje NEM közli (mérve: útiköltség- és
 * postaköltség-bizonylatok, minden hónapban visszatérően). A bizonylat így besorolható, de az összege
 * hiányzik — a havi kiadás-összeg tehát rendszeresen ALUL van becsülve, ráadásul láthatatlanul: az
 * összegen nem látszik, csak ha valaki a listát böngészi.
 *
 * MIÉRT NEM A SZOKÁSOS SZERKESZTÉS: a NAV-sor mezői az adóhatóság rekordjai, azokat a BFF 422-vel védi.
 * Ez a végpont NEM lazítja azt a falat: kizárólag a HIÁNYZÓ összeget engedi pótolni, kizárólag olyan
 * soron, ahol a NAV tényleg nem közölt semmit (`source='nav'` ÉS `amountUnknown`). Minden más soron 422.
 *
 * A HÁROM ÖSSZEG együtt jön, és a `net + áfa === bruttó` fal itt is áll — ugyanaz a szerződés-szintű
 * ellenőrzés, mint a kézi kiadásnál. A felület a bruttót SZÁMOLJA (nincs harmadik, kézzel írt szám); a fal
 * a felületet megkerülő kérés ellen véd, ami némán hibás havi végösszeget írna.
 */
export const ExpenseAmountBodySchema = z.object({
    netAmount: decimalStr,
    vatAmount: decimalStr,
    grossAmount: decimalStr,
});
export const ExpenseAmountBodySchemaChecked = ExpenseAmountBodySchema.refine((v) => addDecimals(v.netAmount, v.vatAmount) === normalizeDecimal(v.grossAmount), {
    message: 'A bruttó összeg nem egyezik a nettó és az áfa összegével.',
    path: ['grossAmount'],
});
/** Elvetés / visszavétel. NAV-soron IS megy: épp az ilyen bizonylatoktól akarunk megszabadulni a listán. */
export const ExpenseDismissBodySchema = z.object({
    dismissed: z.boolean(),
});
/** Kategorizálás / megjegyzés — az egyetlen út, ami NAV-soron is engedélyezett. */
export const ExpenseClassifyBodySchema = z.object({
    categoryId: z.string().uuid().nullable(),
    note: z.string().trim().max(2000).nullable().optional(),
    /**
     * A VONATKOZÁSI HÓNAP kézi beállítása (`YYYY-MM`), vagy `null` → vissza a bizonylatból származtatottra.
     * A besorolással EGY úton jár, mert ugyanaz a munka: a soron eldöntjük, hová tartozik — melyik
     * költség-kategóriába ÉS melyik hónap eredményébe. NAV-soron is engedett (a bizonylat mezőihez nem nyúlunk).
     * Hiányzó mező → a meglévő érték MARAD (a besorolás nem sorolja át csendben a hónapot).
     */
    accountingMonth: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'A vonatkozási hónap alakja YYYY-MM')
        .nullable()
        .optional(),
    /**
     * A MUNKATÁRS beállítása, vagy `null` → nincs személyhez kötve. A besorolással EGY úton jár: ugyanaz a
     * munka dönti el, melyik költség-kategóriába, melyik hónapba ÉS kihez tartozik a sor. NAV-soron is
     * engedett (a bizonylat saját mezőihez nem nyúl). Hiányzó mező → a meglévő érték MARAD.
     */
    employeeIdentityId: z.string().uuid().nullable().optional(),
});
/**
 * TÖMEGES besorolás — a lap fő munkája sorozatban.
 *
 * A KÉT MEZŐ KÜLÖN-KÜLÖN opcionális, de LEGALÁBB AZ EGYIK kötelező: a besorolás és az átsorolás ugyanaz a
 * munka („hová tartozik ez a sor"), és a kettő gyakran együtt jár (a NAV szeptemberben hoz egy köteg
 * augusztusi teljesítésű számlát) — de nem kényszeríthetjük egymásra: a kategória beállítása nem sorolhatja
 * át a hónapot, és fordítva. A HIÁNYZÓ mező jelentése „ne nyúlj hozzá"; a megadott `null` KIFEJEZETT
 * visszaállítás (kategória: besorolatlan · hónap: származtasd a bizonylatból).
 *
 * A DARAB-PLAFON 100, és nem önkényes: a lista lapmérete is 100, tehát „a látott lap kijelölése" belefér —
 * egy sor viszont KÉT Odin-hívás (elő-olvasás + írás), és a busz percenkénti kérés-plafonja 600.
 */
export const ExpenseBulkClassifyBodySchema = z.object({
    ids: z.array(z.string().uuid()).min(1, 'Legalább egy sort ki kell jelölni').max(100),
    categoryId: z.string().nullable().optional(),
    accountingMonth: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Érvénytelen hónap (YYYY-MM)')
        .nullable()
        .optional(),
    /** A MUNKATÁRS kötegelt beállítása (`null` = kifejezett leválasztás, hiányzó = ne nyúlj hozzá). */
    employeeIdentityId: z.string().uuid().nullable().optional(),
});
/**
 * A „legalább az egyik mező" szabály — SZÁNDÉKOSAN nem `.refine` a sémán.
 *
 * A séma-szintű refine bukása a ts-rest body-validálóján **400**-at adna, a repo szerződése viszint a
 * tartalmi hibára **422 + mezőhöz kötött üzenet** (`zod-field-errors`). Ugyanazt a választ kell adnia, mint a
 * „nem létező kategória" ágnak — különben a felület két különböző hiba-alakot kapna ugyanarra a kérdésre.
 */
export function bulkClassifyTouchesSomething(body) {
    return 'categoryId' in body || 'accountingMonth' in body || 'employeeIdentityId' in body;
}
/** A tömeges besorolás eredménye — SOR-SZINTŰ: egy sor hibája nem állítja meg a többit. */
export const ExpenseBulkClassifyResultSchema = z.object({
    summary: z.object({ success: z.number().int(), failed: z.number().int() }),
    /** A BUKOTT sorok (id + ok) — a felület ennyit tud felajánlani újrapróbálásra. */
    failures: z.array(z.object({ id: z.string(), message: z.string() })),
});
/**
 * A hónap összegzése kategóriánként — a Kiadások fül fejléce és az Összegzés fül kiadás-oldala.
 * Devizánként bontva: a kiadás-oldal NEM vált át egy fő devizára (a bevétel-oldal MNB-árfolyama a
 * BEVÉTELRE van hangolva; egy második, hallgatólagos átváltás itt félrevezető összeget adna).
 */
export const ExpenseSummarySchema = z.object({
    month: z.string(),
    /** Devizánkénti végösszeg (nettó/áfa/bruttó). */
    totals: z.array(z.object({
        currency: z.string(),
        netAmount: decimalStr,
        vatAmount: decimalStr,
        grossAmount: decimalStr,
        count: z.number(),
    })),
    /**
     * FŐKATEGÓRIÁNKÉNTI nettó bontás (a BESOROLATLAN `categoryId: null`-lal, ELÖL). A mélyebb szintekre
     * (alkategória, tétel) besorolt tételek az ÁGUK GYÖKERÉHEZ adódnak — a kimutatás így a főkategóriák
     * szintjén olvasható.
     */
    byCategory: z.array(z.object({
        /** A FŐKATEGÓRIA azonosítója (nem a soré) — `null` a besorolatlanoknál. */
        categoryId: z.string().nullable(),
        categoryName: ExpenseCategoryNameSchema.nullable(),
        currency: z.string(),
        netAmount: decimalStr,
        count: z.number(),
    })),
    /** Hány sor vár még besorolásra — a lap fő teendő-jelzése. */
    uncategorized: z.number(),
    /**
     * Hány sor ül BÉR-TÍPUSÚ tételen munkatárs nélkül — a MÁSODIK teendő-jelzés.
     *
     * MIÉRT SZÁMLÁLÓ, és miért nem a soron tárolt „figyelmeztetés": ez ÉLŐ állapot, ami megszűnik, amint
     * valaki kitölti a mezőt. Egy eltárolt jelölés a javítás után is ott maradna, és külön törölni kellene.
     */
    salaryWithoutEmployee: z.number(),
    /**
     * Hány sor összege HIÁNYZIK MÉG: a NAV nem közölte, és kézzel sem pótolták — ezek 0-val szerepelnek a
     * végösszegben, tehát az ALULBECSÜL. A kézzel pótolt sor NEM számít bele (az már valódi összeggel áll).
     */
    unknownAmount: z.number(),
    /**
     * Hány sort vetettünk el a hónapban. A fenti összegek EZEKET NEM tartalmazzák (az elvetés azt jelenti,
     * hogy a tétel nem a mi könyvelésünk dolga) — a szám csak azért van itt, hogy ne tűnjenek el nyomtalanul.
     */
    dismissed: z.number(),
});
// ── Kiadás-ELEMZÉS (a Pénzügy Összegzés grafikonjai) ────────────────────────────────────────────────
/** Egyetlen érték → egyelemű tömb (a lekérdezésben az egy elemű tömb is jöhet sima kulcsként). */
export const ExpenseAnalyticsQuerySchema = z.object({
    /** A CÉL-hónap: a trend az ezzel BEZÁRÓLAG vett 12 hónap, a hónap-bontások erre a hónapra szólnak. */
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Érvénytelen hónap (YYYY-MM)'),
    lang: z.enum(['hu', 'en']).optional(),
    /** Szabad szöveg: a szállító neve, az adószám és a számlaszám (ugyanaz, mint a Kiadások listán). */
    q: z.string().trim().max(200).optional(),
    /**
     * TÍPUS (user-döntés: mind / bér / egyéb): `salary` = bér-típusú (munkatársat kérő) tételbe sorolt sor, `other` =
     * minden más (a besorolatlan is). Hiányzó érték → mind. A bér-jelölés a kategórián él (`requiresEmployee`).
     */
    type: z.enum(['salary', 'other']).optional(),
    /**
     * KATEGÓRIA(K): egy csomópont kiválasztása az EGÉSZ ágát hozza (alkategória, tétel); `none` = besorolatlan.
     */
    categoryId: z.preprocess(toQueryArray, z.array(z.string().max(64)).max(200)).optional(),
    /** KIBOCSÁTÓ (melyik cégünk számlái): `MOR` · `MDIG`. */
    issuerKey: z.string().max(16).optional(),
    /** FORRÁS: NAV-import vagy kézi. */
    source: ExpenseSourceSchema.optional(),
});
/**
 * A kiadás-oldal ELEMZÉSE — ugyanazon a szabályon, mint az összegzés (`ExpenseSummary`): a hónapot a
 * VONATKOZÁSI hónap dönti el, az ELVETETT sor kimarad. A grafikonok CSAK a forint-oldalt mutatják (a lap
 * egyenlege is csak azt vonja össze); a devizás rész hónaponként KÜLÖN, kiírva jön (`foreign`), nem átváltva.
 */
export const ExpenseAnalyticsSchema = z.object({
    month: z.string(),
    /** A 12 hónap (régitől újig, a cél-hónappal bezárólag) — a trend és a kategória-trend tengelye. */
    trend: z.array(z.object({
        month: z.string(),
        /** A hónap FORINT nettó kiadása. */
        netAmount: decimalStr,
        count: z.number(),
        /** A hónap NEM forint kiadása devizánként (átváltás nélkül). */
        foreign: z.array(z.object({ currency: z.string(), netAmount: decimalStr })),
    })),
    /**
     * FŐKATEGÓRIÁNKÉNT a 12 hónap forint nettója (a besorolatlan `categoryId: null`), a 12 havi összeg szerint
     * csökkenően. A `values` a `trend` hónapjaival azonos sorrendű.
     */
    categoryTrend: z.array(z.object({
        categoryId: z.string().nullable(),
        categoryName: ExpenseCategoryNameSchema.nullable(),
        values: z.array(decimalStr),
    })),
    /** A cél-hónap 10 legnagyobb SZÁLLÍTÓJA forint nettó szerint (az adószám a kulcs, annak híján a név). */
    topSuppliers: z.array(z.object({
        supplierName: z.string(),
        supplierTaxNumber: z.string().nullable(),
        netAmount: decimalStr,
        count: z.number(),
    })),
    /**
     * A cél-hónap forint nettója BÉR és EGYÉB bontásban. Bér = a sor bér-típusú (munkatársat kérő) tételbe van
     * sorolva — ugyanaz a jelölés, amin a „munkatárs nélküli bér" teendő áll.
     */
    salarySplit: z.object({ salary: decimalStr, other: decimalStr }),
    /**
     * A cél-hónap FŐKATEGÓRIA-bontása devizánként — UGYANAZ az alak és szabály, mint a lista összegzésénél
     * (`ExpenseSummary.byCategory`), csak a szűrt halmazon: a Kiadás-elemzés kategória-panelje ebből áll.
     */
    byCategory: ExpenseSummarySchema.shape.byCategory,
});
// ── NAV Online Számla import ────────────────────────────────────────────────────────────────────────
/**
 * A kézi lekérés tartománya. Üresen INKREMENTÁLIS (a legutóbb importált bizonylat beérkezés-idejétől
 * mostanáig); `from`+`to` megadva visszamenőleges pótlás. A NAV egy kérésben legfeljebb 35 napot fed le —
 * a hosszabb időszakot a szerver SZELETELI, tehát itt tetszőleges tartomány megadható.
 */
export const NavSyncBodySchema = z
    .object({
    from: dayStr.optional(),
    to: dayStr.optional(),
    /**
     * SZÁRAZ FUTÁS: a lekérés végigmegy (hitelesítés, ablakok, lapozás, duplikátum-szűrés), és megmondja,
     * mit hozna — de EGY sort sem ír. Az első éles bekötés és a nagy visszamenőleges pótlás előtt ez az
     * olcsó bizonyíték: ha a kulcs rossz vagy a tartomány elszámolt, az még írás előtt kiderül.
     */
    dryRun: z.boolean().optional(),
})
    .refine((v) => (v.from === undefined) === (v.to === undefined), {
    message: 'A tartományhoz MINDKÉT dátum kell (vagy egyik sem).',
    path: ['to'],
})
    .refine((v) => v.from === undefined || v.to === undefined || v.from <= v.to, {
    message: 'A kezdő dátum nem lehet későbbi a záró dátumnál.',
    path: ['to'],
})
    // FELSŐ KORLÁT. A szerver 35 napos ablakokra szeletel, és MINDEN ablak legalább egy NAV-kérés: egy
    // évszázadnyi tartomány ezer kérést indítana, órákig futna, és elégetné a NAV fojtott kérés-keretét.
    // Két év bőven fedi a valós visszamenőleges pótlást; ennél több KÉT futás.
    .refine((v) => v.from === undefined ||
    v.to === undefined ||
    (new Date(`${v.to}T00:00:00.000Z`).getTime() -
        new Date(`${v.from}T00:00:00.000Z`).getTime()) /
        86_400_000 <=
        NAV_SYNC_MAX_DAYS, {
    message: 'A lekérés legfeljebb két évet fedhet le — hosszabb időszakot több körben kérj le.',
    path: ['to'],
});
/** A kézi NAV-lekérés maximális tartománya NAPBAN (két év). */
export const NAV_SYNC_MAX_DAYS = 730;
/** Egy import-futás mérlege. A `skipped` NEM hiba: a már nálunk lévő bizonylatokat jelenti. */
export const NavImportReportSchema = z.object({
    /** SZÁRAZ futás volt-e — ilyenkor a `created` azt mondja, hány sor JÖTT VOLNA létre. */
    dryRun: z.boolean(),
    /** `busy` → épp fut egy másik import (a napi cron vagy egy másik kattintás) — NEM hiba, csak várni kell. */
    status: z.enum(['ok', 'partial', 'failed', 'disabled', 'busy']),
    from: z.string().nullable(),
    to: z.string().nullable(),
    fetched: z.number(),
    created: z.number(),
    skipped: z.number(),
    /**
     * Hány digest volt HIÁNYOS (nincs számlaszám / szállító-adószám / dátum) — nem importálható, és nem is
     * fog magától megjavulni. KÜLÖN a `failed`-től: ez a NAV adatán múlik, az a mi írásunkon.
     */
    invalid: z.number(),
    /**
     * Hány sor írása BUKOTT EL nálunk (fojtás, séma-hiba, kiesés). Ez ÚJRAPRÓBÁLHATÓ — és amíg a szám nem
     * nulla, a futás `partial`, a következő inkrementális kör pedig NEM lép túl a legkorábbi bukott
     * bizonylaton. Enélkül a bukott sor némán, VÉGLEG kimaradt: a vízjel a sikeres sorokból számolódik.
     */
    failed: z.number(),
    /**
     * Hány sort vetett el a futás AUTOMATIKUSAN, mert sztornó-pár TELJES lett (a sztornó és az eredetije is
     * nálunk van). Mindig PÁROS szám: a kettőt csak együtt vetjük el.
     *
     * MIÉRT NINCS CÉGENKÉNTI BONTÁSA (a többi számlálóval szemben): a párosítás a TELJES állományon fut, egy
     * körben, a cégenkénti letöltések UTÁN — a sztornó ugyanis beérkezhet egy későbbi futásban, mint az
     * eredetije, és akkor a pár egyik cég futásához sem tartozna.
     */
    dismissed: z.number(),
    /** A NAV hibakódja (a hiba SZÖVEGE sosem jön át: visszhangozhatna bemeneti adatot). */
    errorCode: z.string().nullable(),
    /**
     * CÉGENKÉNTI bontás — a fenti számok ezek összegei.
     *
     * MIÉRT: a NAV-adatszolgáltatás adózónként megy, tehát az egyik cég lejárt kulcsa a másik importját nem
     * érinti. Egyetlen összesített „részleges" elrejtené, MELYIK céggel van baj — pedig a teendő épp az.
     */
    issuers: z.array(z.object({
        issuerKey: z.string(),
        from: z.string().nullable(),
        to: z.string().nullable(),
        fetched: z.number(),
        created: z.number(),
        skipped: z.number(),
        invalid: z.number(),
        failed: z.number(),
        errorCode: z.string().nullable(),
    })),
});
export const NavStatusSchema = z.object({
    /** Be van-e kötve LEGALÁBB EGY cég technikai felhasználója (mind a négy adata megvan). */
    configured: z.boolean(),
    /**
     * A BEÁLLÍTOTT cégek kulcsai (`MOR` · `MDIG`). A NAV-adatszolgáltatás adózónként megy — cégenként külön
     * technikai felhasználóval —, tehát a lap nem „be van kötve / nincs" állapotot mutat, hanem azt, MELYIK
     * cégé él. Egy hiányzó készlet nem hiba: az a cég kimarad, a másik fut tovább.
     */
    issuerKeys: z.array(z.string()),
    mode: z.enum(['test', 'production']),
    /** A legutóbb importált bizonylat NAV-hoz beérkezési ideje (a következő inkrementális futás vízjele). */
    lastInsDate: z.string().nullable(),
    /**
     * Az aláírókulcs lejárata, ha az üzemeltető megadta. A NAV API-n NEM kérdezhető le — a portál mondja meg,
     * és a csere elmulasztása néma import-leállás lenne, ezért a felület figyelmeztet.
     */
    signKeyExpiresAt: z.string().nullable(),
    /** Hány nap van a lejáratig (múltbeli lejáratnál negatív). `null`, ha nincs megadva lejárat. */
    signKeyDaysLeft: z.number().nullable(),
    /**
     * MELYIK cég kulcsa jár le legkorábban. A dátum önmagában nem teendő, ha több technikai felhasználó van —
     * a kezelőnek tudnia kell, melyik portálon kell cserélnie.
     */
    signKeyIssuerKey: z.string().nullable(),
});
// ── TÖMEGES IMPORT (sablon → előnézet → véglegesítés → visszavonás) ────────────────────────────────
//
// A VEZÉRELV: az import NEM tud mást, mint a kézi űrlap. Minden sorból ugyanaz a `ExpenseWriteBody` lesz,
// és ugyanazon a sémán meg ugyanazon az írás-úton megy át. Így egy jövőbeli új szabály (mint a „nincs
// számla") magától érvényes az importra is — egy párhuzamos validációs készlet garantáltan elcsúszna.
/** Egy feltöltött sor NYERS alakja: csupa szöveg, ahogy a táblázatban áll. A feloldás a szerveré. */
export const ExpenseImportRawRowSchema = z.object({
    /** A sor száma a FÁJLBAN (fejléc nélkül, 1-től) — a hibaüzenet enélkül nem mutatna sehova. */
    rowNumber: z.number().int().min(1),
    issuerKey: z.string(),
    supplierName: z.string(),
    supplierTaxNumber: z.string(),
    /**
     * NINCS SZÁMLA jelölés a táblázatból (`nincs` / `van`). Ugyanaz a SZÁNDÉK-nyilatkozat, mint a kézi űrlap
     * kapcsolója: enélkül az üres számlaszám kétértelmű volna — szándékos elhagyás vagy elfelejtett kitöltés.
     */
    noBill: z.string(),
    invoiceNumber: z.string(),
    issueDate: z.string(),
    fulfillmentDate: z.string(),
    dueDate: z.string(),
    netAmount: z.string(),
    vatAmount: z.string(),
    currency: z.string(),
    /** A kategória TELJES ÚTJA (`Advisory bér / Bér (+…)`) — a puszta levél-név NEM kulcs (lásd a feloldót). */
    categoryPath: z.string(),
    /** A munkatárs NEVE, ahogy a címtárban áll (sorrend-tűrő, ékezet- és kisbetű-érzéketlen feloldás). */
    employeeName: z.string(),
    accountingMonth: z.string(),
    note: z.string(),
});
/**
 * Egy sor-szintű lelet. A `field` a FORM mezőneve, hogy a felület a javítás-űrlapon rá tudjon ugrani.
 *
 * BLOKKOLÓ (`error`) vs. FIGYELMEZTETÉS (`warning`): az előbbi nélkül a sor nem is volna felvehető kézzel;
 * az utóbbi egy ÜZLETI gyanú, ami lehet teljesen rendben (két bónusz egy hónapban).
 */
export const ExpenseImportIssueSchema = z.object({
    level: z.enum(['error', 'warning']),
    field: z.string().nullable(),
    message: z.string(),
});
/**
 * A sor VÁZLATA: minden mező, amit sikerült értelmezni — a HIBÁS soron is.
 *
 * MIÉRT KELL a `body` MELLETT: a javító-űrlapot ebből töltjük elő. Ha csak a `body` lenne, egy rossz dátum
 * miatt a sor összes TÖBBI értéke (ár, kategória, munkatárs, megjegyzés) elveszne a javításkor — az űrlap
 * üresen nyílna, és a kezelőnek újra be kellene gépelnie azt, ami a táblázatban és az előnézetben is ott
 * van (user-lelet). Ami nem volt értelmezhető, az `null`: azt az egy mezőt kell kitölteni.
 */
export const ExpenseImportDraftSchema = z.object({
    issuerKey: z.string(),
    supplierName: z.string(),
    supplierTaxNumber: z.string().nullable(),
    noBill: z.boolean(),
    invoiceNumber: z.string().nullable(),
    issueDate: z.string().nullable(),
    fulfillmentDate: z.string().nullable(),
    dueDate: z.string().nullable(),
    netAmount: z.string().nullable(),
    vatAmount: z.string().nullable(),
    currency: z.string(),
    categoryId: z.string().nullable(),
    employeeIdentityId: z.string().nullable(),
    accountingMonth: z.string().nullable(),
    note: z.string().nullable(),
});
/** Egy feloldott sor az előnézetben: a kész body (ha összeállt), a vázlat, a leletek és a CÍMKÉK. */
export const ExpenseImportResolvedRowSchema = z.object({
    rowNumber: z.number(),
    /** `null` → a sorból nem állt össze érvényes body (van blokkoló hibája). A VÉGLEGESÍTÉS ezt küldi. */
    body: ExpenseWriteBodySchema.nullable(),
    /** A JAVÍTÓ-ŰRLAP előtöltése — a hibás soron is kitöltve, amennyire értelmezhető volt. */
    draft: ExpenseImportDraftSchema,
    issues: z.array(ExpenseImportIssueSchema),
    /** A feloldott kategória TELJES ÚTJA, kiírásra — a felület nem old fel újra. */
    categoryLabel: z.string().nullable(),
    /** A feloldott munkatárs megjelenítendő neve. */
    employeeLabel: z.string().nullable(),
});
export const ExpenseImportPreviewBodySchema = z.object({
    rows: z.array(ExpenseImportRawRowSchema).min(1).max(MAX_IMPORT_ROWS),
    lang: z.enum(['hu', 'en']).optional(),
});
export const ExpenseImportPreviewSchema = z.object({
    rows: z.array(ExpenseImportResolvedRowSchema),
    /** Hány soron van BLOKKOLÓ hiba — amíg ez nem nulla, a véglegesítés tiltott. */
    errorRows: z.number(),
    warningRows: z.number(),
    /** Devizánkénti nettó/bruttó összegzés, hogy a véglegesítés előtt látszódjon, mennyi pénz jön be. */
    totals: z.array(z.object({
        currency: z.string(),
        netAmount: decimalStr,
        grossAmount: decimalStr,
        count: z.number(),
    })),
});
export const ExpenseImportCommitBodySchema = z.object({
    /**
     * A köteg azonosítója — a KLIENS állítja elő (UUID), és a köteg minden sora ezt kapja. Egyben a commit
     * idempotencia-kulcsa: a szerver 409-cel utasítja el, ha ezzel az azonosítóval MÁR írt sorokat.
     *
     * MIÉRT a kliens adja: a busz `Idempotency-Key`-t ezen az INTERNAL típuson elutasít, tehát platform-szintű
     * dedup nincs — a dupla kattintás ellen csak egy, a kérés ELŐTT megszületett azonosító véd.
     */
    batchId: z.string().uuid(),
    /** A (szükség esetén az előnézetben JAVÍTOTT) sorok — ugyanaz a body, mint a kézi felvitelen. */
    rows: z.array(ExpenseWriteBodySchema).min(1).max(MAX_IMPORT_ROWS),
});
export const ExpenseImportResultSchema = z.object({
    batchId: z.string(),
    created: z.number(),
    /** A sikertelen sorok — a köteg a visszavonással egyben kivezethető, ha félbemaradt. */
    failed: z.array(z.object({ index: z.number(), message: z.string() })),
});
/** Egy korábbi köteg a „Legutóbbi importok" listán. */
export const ExpenseImportBatchSchema = z.object({
    batchId: z.string(),
    /** Hány ÉLŐ sora van még (a visszavont kötegé 0). */
    rows: z.number(),
    /** A köteg legkorábbi sorának létrejötte. */
    createdAt: z.string().nullable(),
    /** Mely hónapokat érintette (vonatkozási hónap szerint), hogy a lista ne csak azonosítókat mutasson. */
    months: z.array(z.string()),
});
/** A SABLON kitöltést segítő készlete — élő adatból, hogy ne kelljen kategória-utat gépelni. */
export const ExpenseImportTemplateSchema = z.object({
    issuerKeys: z.array(z.string()),
    /** MINDEN választható kategória TELJES ÚTJA (csak levél — gyűjtőre kiadás nem sorolható). */
    categoryPaths: z.array(z.string()),
    /** Azok az utak, amelyeknél a munkatárs megadása VÁRT (bér-típusú tétel). */
    salaryCategoryPaths: z.array(z.string()),
    employeeNames: z.array(z.string()),
    currencies: z.array(z.string()),
});
//# sourceMappingURL=schemas.js.map